import React, { useMemo } from "react";
import {
  Box,
  Tab,
  Tabs,
} from "@mui/material";
import {
  Info,
  Security,
  School,
  CalendarMonth,
  BeachAccess,
} from "@mui/icons-material";
import { useSearchParams } from "react-router-dom";
import { TopLevelPaper } from "../../../themes/StyledComponents";
import { mobiliTheme } from "../../../themes/mobiliTheme";
import BasicUserInfo from "./BasicUserInfo";
import PermissionAssignment from "./Permissions";
import TherapiesAssignment from "./Therapies";
import WeeklyUserCalendar from "./Calendar/WeeklyUserCalendar";
import UserCalendar from "./Calendar/UserCalendar";
import type { UserDto } from "../../../api-client";
import { useUnsavedChanges } from "./UnsavedChangesGuard";

/** String-based tab identifiers — avoids index-shift issues with conditional tabs */
export enum USER_SUBTABS {
  INFO = "info",
  PERMISSIONS = "berechtigung",
  THERAPIES = "fortbildungen",
  WORK_PLAN = "arbeitsplan",
  VACATION = "urlaub",
}

/** Definition of a single tab */
interface UserTab {
  value: USER_SUBTABS;
  label: string;
  icon: React.ReactElement;
  /** Whether this tab should be shown */
  visible: boolean;
}

interface UserRightPanelProps {
  user: UserDto;
  onSelectUser: (user: UserDto | null) => void;
}

/**
 * Right-side panel for User detail view.
 * Horizontal tabs at the top — mirrors PatientRightPanel structure.
 * Tab selection is persisted in URL search params (?userSubTab=...).
 */
const UserRightPanel: React.FC<UserRightPanelProps> = ({ user, onSelectUser }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { guard } = useUnsavedChanges();

  // Read active tab from URL, default to "info"
  const activeTab = (searchParams.get("userSubTab") as USER_SUBTABS) || USER_SUBTABS.INFO;

  const isTherapist = user?.roles?.some((role: { id: string }) => role.id === "therapist");

  // Build tab definitions — filter out invisible ones
  const tabs: UserTab[] = useMemo(
    () =>
      [
        {
          value: USER_SUBTABS.INFO,
          label: "Info",
          icon: <Info />,
          visible: true,
        },
        {
          value: USER_SUBTABS.PERMISSIONS,
          label: "Berechtigung",
          icon: <Security />,
          visible: true,
        },
        {
          value: USER_SUBTABS.THERAPIES,
          label: "Fortbildungen",
          icon: <School />,
          visible: !!isTherapist,
        },
        {
          value: USER_SUBTABS.WORK_PLAN,
          label: "Arbeitsplan",
          icon: <CalendarMonth />,
          visible: true,
        },
        {
          value: USER_SUBTABS.VACATION,
          label: "Urlaub",
          icon: <BeachAccess />,
          visible: true,
        },
      ].filter((tab) => tab.visible),
    [isTherapist]
  );

  const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
    // Switching tabs unmounts the current tab's form, so ask first if it holds
    // edits that were never saved.
    guard(() => {
      // Preserve existing search params, update only userSubTab
      const newParams = new URLSearchParams(searchParams);
      newParams.set("userSubTab", newValue);
      setSearchParams(newParams, { replace: true });
    });
  };

  // If the tab carried over in the URL isn't available for this user (e.g.
  // "fortbildungen" left over from a therapist while a non-therapist is now
  // open), fall back to Info.
  const effectiveTab = tabs.some((t) => t.value === activeTab) ? activeTab : USER_SUBTABS.INFO;

  /** Render the content for the currently active tab */
  const renderTabContent = () => {
    switch (effectiveTab) {
      case USER_SUBTABS.INFO:
        return <BasicUserInfo userData={user} onSelectUser={onSelectUser} />;
      case USER_SUBTABS.PERMISSIONS:
        return <PermissionAssignment userData={user} onSelectUser={onSelectUser} />;
      case USER_SUBTABS.THERAPIES:
        return <TherapiesAssignment userData={user} onSelectUser={onSelectUser} />;
      case USER_SUBTABS.WORK_PLAN:
        return <WeeklyUserCalendar userData={user} />;
      case USER_SUBTABS.VACATION:
        return <UserCalendar userData={user} />;
      default:
        return <BasicUserInfo userData={user} onSelectUser={onSelectUser} />;
    }
  };

  return (
    // Flex column so the paper fills whatever the tab strip leaves over.
    // It used to be `height: 100%` on a sibling that starts *below* the tabs,
    // which made the panel exactly one tab-strip taller than its column and
    // pushed its bottom edge out through the enclosing card.
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <Tabs
        orientation="horizontal"
        value={effectiveTab}
        onChange={handleTabChange}
        aria-label="user-subtabs"
        sx={{ minHeight: "", paddingLeft: mobiliTheme.topLevelPadding, flexShrink: 0 }}
      >
        {tabs.map((tab) => (
          <Tab
            key={tab.value}
            iconPosition="start"
            label={tab.label}
            icon={tab.icon}
            value={tab.value}
          />
        ))}
      </Tabs>

      {/* `1 0 auto`: grow into the spare height, but never shrink below the
          tab's own content. */}
      <TopLevelPaper sx={{ borderTopRightRadius: "0px", flex: "1 0 auto", minHeight: 0 }}>
        {renderTabContent()}
      </TopLevelPaper>
    </Box>
  );
};

export default UserRightPanel;
